import { useContext, useState } from 'react';
import { Link, MetadataContext, Modal, SystemFeaturesContext, Tooltip } from '@/components';
import classNames from 'classnames';
import LinkSimpleLight from '@/assets/linkSimpleLight.svg';
import XLight from '@/assets/xLight.svg';
import CopySimpleLight from '@/assets/copy-simple-light.svg';
import { usePathname } from 'next/navigation';

type StaffToolsButtonModel = {
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
};

const StaffToolsButton = ({ className, tooltipPlacement = 'top' }: StaffToolsButtonModel) => {
  const { realHost } = useContext(SystemFeaturesContext);
  const { pageId, pageTitle } = useContext(MetadataContext);
  const [open, setOpen] = useState(false);
  const pathname = usePathname();

  const closeModal = () => {
    setOpen(false);
  };

  const reportLink = realHost + pathname;
  const reportCMSLink =
    (process.env.NEXT_PUBLIC_CMS_URL || 'https://uat-cms.testing-stylus.com/') +
    'umbraco/#/content/content/edit/' +
    pageId +
    '?mculture=en-GB';

  const stubLink = new URL(pathname, process.env.NEXT_PUBLIC_SITE_URL || 'https://uat.testing-stylus.com/').toString();

  const copyHTMLValueToClipboard = async (link: string, title: string) => {
    try {
      // Check for Clipboard API support
      if (navigator.clipboard && navigator.clipboard.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([`<a href="${link}" target="_blank">${title}</a>`], { type: 'text/html' }),
          }),
        ]);
      }
    } catch (err) {
      console.error('Failed to copy the HTML link: ', err);
    }
  };

  const copyValueToClipboard = async (value: string) => {
    try {
      // Check for Clipboard API support
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
      }
    } catch (err) {
      console.error('Failed to copy the link: ', err);
    }
  };

  return (
    <>
      <Tooltip placement={tooltipPlacement} content="Staff tools">
        <button
          className={classNames('rounded-full p-2 hover:bg-gray-100', className, 'cursor-pointer')}
          onClick={() => setOpen(true)}
        >
          <LinkSimpleLight className="h-7 w-7" />
        </button>
      </Tooltip>
      <Modal
        open={open}
        onClose={closeModal}
        contanerClassName="w-auto h-auto bg-white !rounded-none lg:!rounded-md disable-scroll"
      >
        <div className="flex w-[500px] flex-col gap-4 px-6 py-4">
          <div className="flex flex-row items-center justify-between gap-3">
            <div className="text-xl leading-8 tracking-[0.25px]">Staff Tools</div>
            <button className="btn icon" onClick={closeModal}>
              <XLight className="h-7 w-7" />
            </button>
          </div>
          <div className="flex flex-col gap-4 text-sm leading-[22px] tracking-[0.1px] text-black/60">
            <div className="flex flex-row justify-between gap-2">
              <div className="w-3/12 font-semibold">Report name</div>
              <Link
                className="w-7/12 overflow-hidden break-all underline"
                link={{ href: reportLink, target: '_blank' }}
              >
                {pageTitle}
              </Link>
              <div className="w-2/12 text-right">
                <button className="btn icon" onClick={() => copyHTMLValueToClipboard(reportLink, pageTitle)}>
                  <CopySimpleLight className="h-7 w-7" />
                </button>
              </div>
            </div>
            <div className="flex flex-row justify-between gap-2">
              <div className="w-3/12 font-semibold">CMS link</div>
              <Link
                className="w-7/12 overflow-hidden break-all underline"
                link={{ href: reportCMSLink, target: '_blank' }}
              >
                {reportCMSLink}
              </Link>
              <div className="w-2/12 text-right">
                <button className="btn icon" onClick={() => copyValueToClipboard(reportCMSLink)}>
                  <CopySimpleLight className="h-7 w-7" />
                </button>
              </div>
            </div>
            <div className="flex flex-row justify-between gap-2">
              <div className="w-3/12 font-semibold">Stub link</div>
              <Link className="w-7/12 overflow-hidden break-all underline" link={{ href: stubLink, target: '_blank' }}>
                {stubLink}
              </Link>
              <div className="w-2/12 text-right">
                <button className="btn icon" onClick={() => copyValueToClipboard(stubLink)}>
                  <CopySimpleLight className="h-7 w-7" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default StaffToolsButton;
